import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { redirect } from "react-router-dom";
import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";

// Lazy so the route file doesn't eagerly pull the tabs pages (which import
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const NotificationsLayout = lazy(
  () => import("@/pages/(home)/(common)/notifications/layout"),
);
const NotificationsAllPage = lazy(
  () => import("@/pages/(home)/(common)/notifications/all"),
);
const NotificationsUnreadPage = lazy(
  () => import("@/pages/(home)/(common)/notifications/unread"),
);
const Announcement = lazy(() => import("@/pages/(home)/(common)/announcements"));
const AnnouncementsAllPage = lazy(
  () => import("@/pages/(home)/(common)/announcements/all"),
);
const AnnouncementsUnreadPage = lazy(
  () => import("@/pages/(home)/(common)/announcements/unread"),
);

// ─── Path Segments ────────────────────────────────────────────────────────────
export enum COMMON_PATH_SEGMENTS {
  NOTIFICATIONS = "notifications",
  ANNOUNCEMENTS = "announcements",
  // Shared generic tab segments — reused under BOTH parents above (same
  // literal "all"/"unread" segment, nested under a different parent path).
  TAB_ALL = "all",
  TAB_UNREAD = "unread",
}

// ─── Route Definitions  ─────────────────────────────────
export const commonRoutes: RouteObject[] = [
  {
    // This is the my-space-reachable /notifications route only.
    // /company/notifications is a SEPARATE route (routes/company/routes.notifications.ts)
    // reusing the original scope-aware `Notifications` component directly —
    // untouched by this conversion.
    path: COMMON_PATH_SEGMENTS.NOTIFICATIONS,
    HydrateFallback: Spinner,
    handle: { breadcrumb: "Notifications", title: "Notifications" },
    children: [
      // ── Page-level tabs layout: each tab owns its own table ──────────────
      {
        Component: NotificationsLayout,
        handle: { flushContent: true },
        children: [
          {
            index: true,
            loader: () => redirect(COMMON_PATHS.NOTIFICATIONS_ALL),
          },
          {
            path: COMMON_PATH_SEGMENTS.TAB_ALL,
            handle: { breadcrumb: "All" },
            Component: NotificationsAllPage,
          },
          {
            path: COMMON_PATH_SEGMENTS.TAB_UNREAD,
            handle: { breadcrumb: "Unread" },
            Component: NotificationsUnreadPage,
          },
        ],
      },
    ],
  },
  {
    path: COMMON_PATH_SEGMENTS.ANNOUNCEMENTS,
    HydrateFallback: Spinner,
    handle: { breadcrumb: "Announcements", title: "Announcements" },
    children: [
      // ── Page-level tabs layout: each tab owns its own table ──────────────
      {
        Component: Announcement,
        handle: { flushContent: true },
        children: [
          {
            index: true,
            loader: () => redirect(COMMON_PATHS.ANNOUNCEMENTS_ALL),
          },
          {
            path: COMMON_PATH_SEGMENTS.TAB_ALL,
            handle: { breadcrumb: "All" },
            Component: AnnouncementsAllPage,
          },
          {
            path: COMMON_PATH_SEGMENTS.TAB_UNREAD,
            handle: { breadcrumb: "Unread" },
            Component: AnnouncementsUnreadPage,
          },
        ],
      },
    ],
  },
];

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMMON_PATHS = Object.freeze({
  NOTIFICATIONS: addSlashes(COMMON_PATH_SEGMENTS.NOTIFICATIONS),
  NOTIFICATIONS_ALL: addSlashes(
    COMMON_PATH_SEGMENTS.NOTIFICATIONS,
    COMMON_PATH_SEGMENTS.TAB_ALL,
  ),
  NOTIFICATIONS_UNREAD: addSlashes(
    COMMON_PATH_SEGMENTS.NOTIFICATIONS,
    COMMON_PATH_SEGMENTS.TAB_UNREAD,
  ),
  ANNOUNCEMENTS: addSlashes(COMMON_PATH_SEGMENTS.ANNOUNCEMENTS),
  ANNOUNCEMENTS_ALL: addSlashes(
    COMMON_PATH_SEGMENTS.ANNOUNCEMENTS,
    COMMON_PATH_SEGMENTS.TAB_ALL,
  ),
  ANNOUNCEMENTS_UNREAD: addSlashes(
    COMMON_PATH_SEGMENTS.ANNOUNCEMENTS,
    COMMON_PATH_SEGMENTS.TAB_UNREAD,
  ),
});
